import type { AvatarFxKey, BadgeKey, BannerFxKey, FrameKey } from '@anidex/shared/economy';

export type BadgeShape =
  | 'shield'
  | 'crest'
  | 'rosette'
  | 'star'
  | 'hex'
  | 'circle'
  | 'squircle'
  | 'pennant'
  | 'medal'
  | 'octagon'
  | 'teardrop'
  | 'diamond'
  | 'gem'
  | 'heart'
  | 'cog';

type Palette = readonly [string, string, string, string, string];

const PALETTES = {
  bronze: ['#6b3f1d', '#ffd2a1', '#c47a3a', '#7a4419', '#3a1d08'],
  silver: ['#59606e', '#ffffff', '#c9ced8', '#7d8594', '#26303f'],
  gold: ['#7a5410', '#fff3c4', '#f0b429', '#8a6416', '#3d2a05'],
  emerald: ['#0c5a43', '#b8ffe4', '#1fa37a', '#0a4a37', '#022b1f'],
  sapphire: ['#13306b', '#cfe0ff', '#3d7bff', '#17378a', '#061a40'],
  ruby: ['#6b0f1f', '#ffc2cb', '#e0384f', '#8b0d1f', '#3a020b'],
  amethyst: ['#3d1a6b', '#e9d6ff', '#a35bff', '#4b1d8a', '#1d0640'],
  obsidian: ['#000000', '#8a7bb3', '#2b2440', '#07060b', '#e9d6ff'],
  pearl: ['#b8a98f', '#ffffff', '#f5e6c8', '#d9c7a3', '#6b5530'],
  jade: ['#2d5a3a', '#d8ffe0', '#6fbf8a', '#2f7a4a', '#0f2e18'],
  copper: ['#5a2b10', '#ffc79e', '#d07a3e', '#8a4318', '#2e1205'],
  ice: ['#2f6f9a', '#ffffff', '#a8e6ff', '#5fbfff', '#0f3b5c'],
  sakura: ['#b8556f', '#fff0f4', '#ffb7c5', '#e07894', '#5c1a2c'],
  neon: ['#2a0a3a', '#ffd1f7', '#ff3dc8', '#8a0a6b', '#ffffff'],
  ember: ['#5a1206', '#ffd29d', '#ff7a2b', '#a8320c', '#2a0700'],
  forest: ['#1d3b1f', '#c8f0b0', '#5f8a32', '#2f5a2a', '#0e1f0b'],
  ocean: ['#0d3b5c', '#b8ecff', '#2fa6d6', '#145a8a', '#021c2e'],
  sunset: ['#7a2b3a', '#ffe0b8', '#ff8a5c', '#c2410c', '#3a0a12'],
  midnight: ['#050a1f', '#8fa8ff', '#1e2a6b', '#0b1440', '#dfe6ff'],
  divine: ['#c9a24a', '#ffffff', '#fff6e0', '#f0d48a', '#8a6416'],
  slate: ['#2d3038', '#d9dde5', '#6b717d', '#3a3f4a', '#ffffff'],
  crimson: ['#3a0509', '#ffe08a', '#b3122e', '#5b0f1f', '#ffe08a'],
  teal: ['#0b4a4a', '#c8fff6', '#1fbfae', '#0f6b63', '#022b28'],
  lilac: ['#5b3fa8', '#ffffff', '#c9b8ff', '#8a73d9', '#2a1660'],
  lime: ['#3b5a0a', '#f4ffc8', '#a8d63d', '#5f8a12', '#1a2a02'],
  rose: ['#7a4a3a', '#ffe8dc', '#e8a48a', '#b0705a', '#3a1a10'],
  charcoal: ['#111111', '#ff9a9a', '#3a3a40', '#1a1a1f', '#ff4f4f'],
  royal: ['#2a0b40', '#ffe6a8', '#7a3aa0', '#4b1d6b', '#ffe6a8'],
  aqua: ['#0f5f6b', '#e0fdff', '#5fe0f0', '#1aa3b8', '#023a42'],
  sand: ['#7a6a4a', '#fff6dc', '#e0c890', '#a8905a', '#3a2e14'],
} as const satisfies Record<string, Palette>;

export interface BadgeLook {
  shape: BadgeShape;
  /** lucide-react icon name */
  icon: string;
  palette: Palette;
}

function badge(shape: BadgeShape, icon: string, palette: keyof typeof PALETTES): BadgeLook {
  return { shape, icon, palette: PALETTES[palette] };
}

export const BADGE_LOOKS: Record<BadgeKey, BadgeLook> = {
  archive_keeper: badge('shield', 'Archive', 'gold'),
  legendary_ten: badge('crest', 'Castle', 'crimson'),
  insolent_luck: badge('rosette', 'Clover', 'emerald'),
  first_divine: badge('star', 'Sparkles', 'divine'),
  spent_forge: badge('hex', 'Flame', 'ember'),
  booster_storm: badge('circle', 'Tornado', 'sapphire'),
  echo_chamber: badge('squircle', 'AudioLines', 'lilac'),
  market_mogul: badge('pennant', 'Store', 'rose'),
  quest_veteran: badge('medal', 'Medal', 'bronze'),
  streak_thirty: badge('octagon', 'CalendarCheck', 'teal'),
  album_sprout: badge('teardrop', 'Sprout', 'lime'),
  series_lantern: badge('diamond', 'Clapperboard', 'sunset'),
  rare_spark: badge('gem', 'Gem', 'aqua'),
  shard_chip: badge('hex', 'Diamond', 'ice'),
  first_polish: badge('circle', 'Hammer', 'slate'),
  curious_eye: badge('teardrop', 'Eye', 'ocean'),
  deja_vu_mark: badge('squircle', 'Copy', 'midnight'),
  stall_bell: badge('shield', 'Bell', 'sand'),
  handshake: badge('heart', 'Handshake', 'rose'),
  novice_ribbon: badge('pennant', 'Ribbon', 'sapphire'),
  hex_dice: badge('hex', 'Dices', 'amethyst'),
  fox_mask: badge('crest', 'VenetianMask', 'sunset'),
  torii_gate: badge('octagon', 'Landmark', 'ruby'),
  hourglass: badge('diamond', 'Hourglass', 'sand'),
  anchor: badge('shield', 'Anchor', 'ocean'),
  comet: badge('star', 'Orbit', 'midnight'),
  vial: badge('teardrop', 'FlaskConical', 'jade'),
  sword_shield: badge('shield', 'Swords', 'silver'),
  moon_rabbit: badge('circle', 'Rabbit', 'pearl'),
  koi: badge('circle', 'Fish', 'teal'),
  cherry_seal: badge('rosette', 'Flower2', 'sakura'),
  oni_fang: badge('crest', 'Skull', 'charcoal'),
  shuriken: badge('star', 'Asterisk', 'slate'),
  fan: badge('pennant', 'Fan', 'lilac'),
  lantern: badge('teardrop', 'Lamp', 'ember'),
  drum: badge('octagon', 'Drum', 'bronze'),
  wave_crest: badge('hex', 'Waves', 'ocean'),
  crane: badge('diamond', 'Bird', 'pearl'),
  dragon_scale: badge('shield', 'Shell', 'emerald'),
  phoenix: badge('star', 'Feather', 'ember'),
  ink_stamp: badge('squircle', 'Stamp', 'charcoal'),
  vinyl: badge('circle', 'Disc3', 'obsidian'),
  cassette: badge('squircle', 'CassetteTape', 'neon'),
  pixel_coin: badge('octagon', 'Coins', 'gold'),
  neon_bolt: badge('diamond', 'Zap', 'neon'),
  frost_flake: badge('rosette', 'Snowflake', 'ice'),
  ember_coal: badge('hex', 'FlameKindling', 'copper'),
  vine: badge('teardrop', 'Leaf', 'forest'),
  gear: badge('cog', 'Cog', 'copper'),
  crown_chip: badge('gem', 'Crown', 'royal'),
};

export const FRAME_NODES: Record<FrameKey, readonly [ring: number, border: number]> = {
  premium_orbit: [4, 4],
  crystal: [1, 1],
  awakened: [0, 0],
  gold_filigree: [4, 2],
  shimenawa: [2, 3],
  thorns: [0, 2],
  chains: [0, 1],
  rune_ring: [0, 1],
  dragon_scale: [0, 0],
  gears: [1, 1],
  nacre: [0, 0],
  laurel: [8, 4],
  barbed: [8, 2],
  halo_ring: [0, 0],
  pixel_bezel: [0, 4],
  neon_tube: [0, 1],
  ink_brush: [3, 2],
  frost_rim: [5, 1],
  flame_rim: [6, 1],
  gothic_arch: [2, 1],
  circuit: [4, 4],
  stained: [0, 1],
  stamp_edge: [1, 1],
  manga_panel: [1, 0],
  aurora_ring: [0, 0],
  obsidian: [1, 1],
  sakura: [5, 4],
  chainmail: [0, 0],
  compass: [4, 2],
  eclipse: [1, 1],
  void_ring: [0, 0],
  senita_nebula: [8, 4],
};

export const AVATAR_FX_NODES: Record<AvatarFxKey, readonly [inside: number, back: number, front: number]> = {
  premium_stardust: [4, 0, 0],
  explorer: [2, 0, 1],
  petals: [4, 0, 0],
  frost: [3, 0, 0],
  neko_ears: [0, 2, 0],
  horns: [0, 2, 0],
  halo: [0, 0, 1],
  wings: [0, 2, 0],
  scanlines: [2, 0, 0],
  glitch: [3, 0, 0],
  kitsune: [0, 4, 0],
  summoning: [2, 1, 0],
  hud_reticle: [5, 0, 0],
  sparkles: [4, 0, 0],
  rain: [5, 0, 0],
  embers: [4, 0, 0],
  bubbles: [4, 0, 0],
  vines: [0, 0, 2],
  static: [1, 0, 0],
  moonlight: [1, 0, 1],
  sunglasses: [3, 0, 0],
  blush: [2, 0, 0],
  demon_eyes: [2, 0, 0],
  angel_feathers: [3, 0, 0],
  pirate: [3, 0, 0],
  crown: [0, 0, 1],
  headphones: [0, 1, 3],
  mask_oni: [2, 0, 0],
  butterflies: [0, 0, 3],
  constellation: [1, 0, 0],
  binary: [4, 0, 0],
};

export const BANNER_FX_NODES: Record<BannerFxKey, number> = {
  premium_constellation: 5,
  omnibus: 8,
  divine_trio: 3,
  trade_baron: 5,
  summit: 2,
  team_spirit: 1,
  aurora: 3,
  neon_rain: 6,
  great_wave: 1,
  speed_lines: 1,
  halftone: 1,
  code_rain: 5,
  sky_lanterns: 5,
  nebula: 3,
  sakura_storm: 8,
  embers_fall: 6,
  snow: 8,
  lightning: 3,
  film_grain: 3,
  vhs: 3,
  gold_dust: 8,
  ink_wash: 3,
  constellation: 1,
  grid: 2,
  spotlight: 2,
  confetti: 8,
  fireflies: 6,
  ocean: 4,
  ashen: 6,
  prism: 2,
  shrine_night: 2,
  senita_cosmos: 18,
};
